/**
 * @file schemas/market-sampling.ts
 * @description 市售抽樣查驗統計報表共享契約（客戶 PPT S25 第 2 部分 / S27）。
 *
 * 業務背景：
 * - 系統自動統計該年度各驗證方案的市售抽樣（samples.samplingType='年度後市場採樣'）比例，
 *   分四個分類：有機(農糧) / 有機(加工分流) / 產銷履歷(農糧) / 產銷履歷(加工分流)。
 * - 每分類市售抽樣件數須達「該類總數」的 3% 以上（紅線 {@link MARKET_SAMPLING_TARGET_RATIO}）。
 * - 「該類總數」口徑：當年度（案件編號年度）已發證且有效
 *   （workflowState ∈ 已發證/已寄送/結案）案件之**去重客戶數**。
 * - 後市場抽查統計另以客戶維度摘要（{@link ClientMarketSamplingSummary}）連結到
 *   驗證決定表單，供委員審議時查看該客戶的市售抽查情況（S27）。
 *
 * 分類判別規則（{@link classifyMarketSamplingCategory}，純函數可測）：
 * - scheme：TOP → 有機（organic）；TAP → 產銷履歷（tap）；ECO 及其他 → 不納入（null）。
 * - 農糧 vs 加工分流：cases.verificationCategories（jsonb 字串陣列，值來自
 *   verification_categories 字典：TOP=有機作物/有機加工/有機分裝/有機流通；
 *   TAP=農糧產品/農糧加工品/農糧產品分裝/農糧產品流通）任一含
 *   「加工」「分裝」「流通」→ 加工分流；否則（含空/缺失）→ 農糧。
 *
 * 本檔只放類型、常量與純函數，供 API 與 Web 共用。
 */
import { z } from 'zod';
// ─── 常量 ────────────────────────────────────────────────────────────────────
/** 市售抽樣達標紅線：每分類市售抽樣件數 ≥ 該類總數的 3%。 */
export const MARKET_SAMPLING_TARGET_RATIO = 0.03;
/** 四個統計分類 key（順序即報表展示順序）。 */
export const MARKET_SAMPLING_CATEGORY_KEYS = [
    'organic-farm',
    'organic-processing',
    'tap-farm',
    'tap-processing',
];
/** 分類 key → 中文標籤。 */
export const MARKET_SAMPLING_CATEGORY_LABELS = {
    'organic-farm': '有機（農糧）',
    'organic-processing': '有機（加工分流）',
    'tap-farm': '產銷履歷（農糧）',
    'tap-processing': '產銷履歷（加工分流）',
};
/**
 * 「加工分流」判別關鍵詞：驗證類別名稱含任一關鍵詞即歸加工分流。
 * 對齊 verification_categories 字典：有機加工/有機分裝/有機流通、
 * 農糧加工品/農糧產品分裝/農糧產品流通。
 */
export const PROCESSING_CATEGORY_KEYWORDS = ['加工', '分裝', '流通'];
// ─── 純函數：分類判別 ────────────────────────────────────────────────────────
/**
 * 判別案件屬於哪個市售抽樣統計分類。
 *
 * @param scheme 驗證方案（cases.scheme：TOP / TAP / ECO）
 * @param verificationCategories 驗證類別（cases.verificationCategories jsonb，
 *   期望為字串陣列；容忍 null / 非陣列 / 非字串元素——一律當作無加工分流訊號）
 * @returns 分類 key；ECO 或未知 scheme 不納入統計，回傳 null
 */
export function classifyMarketSamplingCategory(scheme, verificationCategories) {
    // 只統計 TOP（有機）與 TAP（產銷履歷）；ECO 及未知方案不納入
    const prefix = scheme === 'TOP' ? 'organic' : scheme === 'TAP' ? 'tap' : null;
    if (!prefix)
        return null;
    // verificationCategories 任一含「加工/分裝/流通」→ 加工分流；否則農糧
    const cats = Array.isArray(verificationCategories) ? verificationCategories : [];
    const isProcessing = cats.some((cat) => typeof cat === 'string' && PROCESSING_CATEGORY_KEYWORDS.some((kw) => cat.includes(kw)));
    return `${prefix}-${isProcessing ? 'processing' : 'farm'}`;
}
/**
 * 計算單一分類的 3% 最低抽樣家數與缺口。
 *
 * 規則不是四捨五入，而是向上取整；只要母數大於 0，最低至少抽 1 家。
 */
export function calculateMarketSamplingRequirement(totalCount, sampleCount) {
    const safeTotal = Math.max(0, Math.trunc(totalCount));
    const safeSamples = Math.max(0, Math.trunc(sampleCount));
    const requiredCount = safeTotal > 0 ? Math.max(1, Math.ceil(safeTotal * MARKET_SAMPLING_TARGET_RATIO)) : 0;
    return {
        requiredCount,
        shortfall: Math.max(0, requiredCount - safeSamples),
        met: safeSamples >= requiredCount,
    };
}
/** 年度報表簽核（簽名檔可選，兼容組長尚未維護員工簽名）。 */
export const signMarketSamplingReportSchema = z.object({
    signatureFileId: z.number().int().positive().optional(),
});
